import { useEffect, useRef, useState } from 'react'

/**
 * Owns the playback logic for a single voice-memo `<audio>` element: play/pause, current
 * time, duration, buffered ("loaded") amount, volume/mute, playback speed and seeking.
 *
 * Attach the returned `audioRef` to the `<audio>` element. Seeking is constrained to
 * already-buffered data: the pods endpoint doesn't support HTTP range requests, so seeking
 * to a not-yet-downloaded byte makes the element re-fetch from 0 and restart. Mirroring the
 * old native player, out-of-buffer seeks are ignored and the thumb snaps back to the real
 * position instead of jumping to the start.
 */
export function useAudioPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null)
  // While the user is dragging the scrubber we drive the value locally and ignore the
  // element's timeupdate events, otherwise the stale audio position fights the drag.
  const seekingRef = useRef(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => {
      if (!seekingRef.current) setCurrentTime(audio.currentTime)
    }
    const updateDuration = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration)
    }
    // Track how much of the clip has downloaded, to show a "loaded" indicator.
    const updateBuffered = () => {
      if (audio.buffered.length > 0) setBuffered(audio.buffered.end(audio.buffered.length - 1))
    }
    // Drive isPlaying from the element's own events so the icon always reflects the real
    // state — even if play() is rejected (autoplay policy, interrupted by pause, etc.).
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('durationchange', updateDuration)
    audio.addEventListener('progress', updateBuffered)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('durationchange', updateDuration)
      audio.removeEventListener('progress', updateBuffered)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handlePause)
    }
  }, [])

  // Keep the native element in sync with the volume/mute/speed controls.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
      audioRef.current.muted = muted
    }
  }, [volume, muted])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  // Toggle from the element's real paused state; the play/pause listeners update isPlaying.
  // Swallow the play() rejection (interrupted / autoplay policy) to avoid console noise.
  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      audio.play()?.catch(() => {})
    } else {
      audio.pause()
    }
  }

  // Drive the thumb locally while dragging; don't touch the audio element yet.
  const handleSeek = (_: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value
    seekingRef.current = true
    setCurrentTime(next)
  }

  // The pods stream has no HTTP range support, so changing currentTime can make the element
  // re-fetch and restart from 0 — unreliable in both directions. Like the old native player,
  // we never commit a seek: on release we just snap the thumb back to the real playback
  // position, leaving playback untouched.
  const handleSeekCommitted = () => {
    const audio = audioRef.current
    if (audio) setCurrentTime(audio.currentTime)
    seekingRef.current = false
  }

  const toggleMute = () => {
    setMuted(prev => !prev)
  }

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value
    setVolume(next)
    setMuted(next === 0)
  }

  const isMuted = muted || volume === 0
  const bufferedPct = duration > 0 ? Math.min(100, (buffered / duration) * 100) : 0

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    bufferedPct,
    playbackRate,
    togglePlay,
    handleSeek,
    handleSeekCommitted,
    toggleMute,
    handleVolumeChange,
    setPlaybackRate,
  }
}
